<script setup lang="ts">
import VuePdfEmbed from 'vue-pdf-embed'
import { useDrawingStore } from '@/store/drawing'
import { useUserStore } from '@/store/user'
import { useSnackbarStore } from '@/store/snackbar'
import { $updateKopItem, $getKopItem, $getKopItemOptions, $approveKopItem } from '@/api/kop'
import { $getCampaign } from '@/api/campaigns'
import ability from '@/plugins/casl/ability'
import type { KopItem, Artwork } from '@/models/kop/kopItem'
import { getFirstPdfLink } from '@/helpers/utils/dropboxLinks'
import { downloadArtworkOrProof } from '@/helpers/utils/downloadAllFilesUsingLinks'
import FileManagementPhotosBlock from '@/components/projects/signs/sign-modal/FileManagementPhotosBlock.vue'
import EditKopItemCode from '@/components/projects/kop/kop-item-modal/EditKopItemCode.vue'

interface IProps {
  kopItemDialogVisible?: boolean,
  isKopItemList?: boolean,
  selectedKopItemId?: string | number | null,
}

const props = withDefaults(defineProps<IProps>(), {
  kopItemDialogVisible: false,
  isKopItemList: false,
  selectedKopItemId: null,
})

interface Emit {
  (e: 'update:kopItemDialogVisible', value: boolean): void
  (e: 'onUpdate'): void
}

const emit = defineEmits<Emit>()

const computedKopItemDialogVisible = computed({
  // getter
  get() {
    if (props.isKopItemList) {
      return props.kopItemDialogVisible
    } else {
      return drawingStore.kopItemDialogVisible
    }
  },
  // setter
  set(newValue) {
    if (props.isKopItemList) {
      emit('update:kopItemDialogVisible', newValue)
    } else {
      drawingStore.kopItemDialogVisible = newValue
    }
  }
})

const computedKopItemId = computed(() => {
  return props.isKopItemList ? props.selectedKopItemId : drawingStore.selectedItem?.kop_item
})

const computedOfflineMode = computed(() => {
  return props.isKopItemList ? false : drawingStore.offlineMode
})

const currentKopItemProject = ref<any>(null)
const currentKopItemFuncAreas = ref<any[]>([])

const computedFuncAreas = computed(() => {
  return props.isKopItemList ? currentKopItemFuncAreas.value : drawingStore.funcAreas
})

const router = useRouter()
const route = useRoute()

const dialogVisible = ref<boolean>(false)
const drawingStore = useDrawingStore()
const userStore = useUserStore()
const snackbarStore = useSnackbarStore()

const kopItem = ref<KopItem>({})
const kopItemLoading = ref<boolean>(false)
const kopItemPriorityOptions = ref([])
const kopItemArtworks = ref<Artwork[]>([])

const updateBtnLoading = ref<boolean>(false)
const showEditCodeDialog = ref<boolean>(false)

const showDeleteConfirmDialog = ref<boolean>(false)

const photoTab = ref<number>(0)

const artworkPdf = ref<InstanceType<typeof VuePdfEmbed> | null>(null)
const artworkLoading = ref<boolean>(true)
const page = ref<number>(1)
const totalPages = ref<number>(1)
const artworkLink = computed(() => {
  const links = Array.isArray(kopItem.value?.artwork_file_links)
    ? kopItem.value.artwork_file_links.map((item: any) => item?.url).filter((url: any): url is string => !!url)
    : []

  return getFirstPdfLink(links)
})

const hasArtworkDownloadSource = computed(() =>
  !!(Array.isArray(kopItem.value?.artwork_file_links) && kopItem.value.artwork_file_links.length)
)

const artworkDownloadLoading = ref<boolean>(false)
const downloadArtwork = async () => {
  if (!hasArtworkDownloadSource.value) return
  artworkDownloadLoading.value = true
  try {
    const downloadLinks = Array.isArray(kopItem.value?.artwork_file_links)
      ? kopItem.value.artwork_file_links
        .map((item: any) => item?.url)
        .filter((url: any): url is string => typeof url === 'string' && !!url)
      : []

    const result = await downloadArtworkOrProof(
      kopItem.value?.campaign,
      kopItem.value?.main_full_code ?? kopItem.value?.kop_item_code,
      'artwork',
    )
    if (result.status === 'complete') {
      snackbarStore.showMessage({ color: 'success', icon: 'mdi-checkbox-marked-circle', title: 'Download Complete', text: 'Artwork downloaded successfully!' })
    } else {
      snackbarStore.showMessage({ color: 'error', icon: 'mdi-alert-circle', title: 'Download Failed', text: 'Failed to download artwork' })
    }
  } catch (e: any) {
    snackbarStore.showMessage({ color: 'error', icon: 'mdi-alert-circle', title: 'Download Failed', text: 'Unexpected error while downloading' })
  } finally {
    artworkDownloadLoading.value = false
  }
}

const sides = ['A', 'B', 'C', 'D']
const kopItemSides = ref<number>(1)

const showSidebar = ref<boolean>(false)

watch(artworkLink, link => {
  artworkLoading.value = !!link
}, { immediate: true })

const isDisabledKopItemFields = computed(() => {
  return !(isUserCanChangeKopItem.value && kopItem.value?.is_active_campaign)
})

const isUserCanChangeKopItem = computed(() => {
  return ability.can('change', 'kopitem')
})

const isUserCanDeleteKopItem = computed(() => {
  return ability.can('delete', 'kopitem')
})

const showKopItemActivityQuery = computed(() => {
  return route.query['show-kop-item-activity']
})

const syncKopItem = (data: KopItem) => {
  kopItem.value = data
  const arr = [...(data.artworks_data || [])]
  kopItemArtworks.value = arr.length ? arr : [{ width: 0, height: 0, qty: 1 }]

  if (!kopItem.value.width) {
    kopItem.value.width = data?.kop_item_type_info?.width || ''
  }

  if (!kopItem.value.height) {
    kopItem.value.height = data?.kop_item_type_info?.height || ''
  }

  kopItem.value.is_custom = data?.kop_item_type_info?.is_custom || false
}

const updateDrawingStoreItem = (data: KopItem) => {
  const index = drawingStore.items.findIndex(i => i.kop_item === data?.id)
  drawingStore.items[index].kop_item_status = data?.item_status
  drawingStore.items[index].kop_item_type = data?.kop_item_type
  drawingStore.items[index].kop_item_type_form = data?.kop_item_type_info?.form_type
  drawingStore.items[index].kop_item_functional_area = data?.functional_area
  drawingStore.items[index].kop_item_code = data?.kop_item_code
  drawingStore.items[index].kop_item_short_code = data?.short_code
  drawingStore.items[index].kop_item_qty = data?.qty
  drawingStore.items[index].kop_item_new_colour = data?.kop_item_new_colour
  drawingStore.items[index].is_released = data?.is_released
  drawingStore.items[index].is_can_user_approve = data?.is_can_user_approve

  // Update initial items
  drawingStore.updateInitialItems(drawingStore.items)
}

const openEditCodeDialog = () => {
  showEditCodeDialog.value = true
}

const onMainFullCodeUpdated = (data: KopItem) => {
  syncKopItem(data)

  if (!props.isKopItemList) {
    updateDrawingStoreItem(data)
  } else {
    emit('onUpdate')
  }
}

const getKopItemOptions = async () => {
  try {
    const res = await $getKopItemOptions()
    kopItemPriorityOptions.value = res.data.actions.POST.priority.choices
  } catch (e: any) {
    console.log(e)
  }
}

const getKopItemProject = async () => {
  if (!kopItem.value?.campaign) return
  try {
    const res = await $getCampaign(kopItem.value.campaign)
    currentKopItemProject.value = res.data
    currentKopItemFuncAreas.value = res.data.functional_area_list
  } catch (e: any) {
    console.log(e)
  }
}

const onSelectPriority = async (priorityValue: number) => {
  if (!kopItem.value?.id) return

  if (priorityValue !== kopItem.value.priority) {
    kopItemLoading.value = true
    try {
      const res = await $updateKopItem(kopItem.value.id, { priority: priorityValue })
      syncKopItem(res.data)
      // No need to update Store Item
      snackbarStore.showMessage({
        color: 'success',
        icon: 'mdi-checkbox-marked-circle',
        title: 'Success',
        text: 'KOP Item Priority updated successfully!'
      })
    } catch (e: any) {
      console.log(e)
      const mes = e?.response?.data ? e?.response?.data : e?.message
      snackbarStore.showMessage({
        color: 'error',
        icon: 'mdi-alert-circle',
        title: 'Error',
        text: `Oops, something went wrong. ${mes}`
      })
    } finally {
      kopItemLoading.value = false
    }
  }
}

const updateKopItemIsOnHold = async () => {
  if (!kopItem.value?.id) return

  kopItemLoading.value = true
  try {
    const res = await $updateKopItem(kopItem.value.id, { is_on_hold: kopItem.value.is_on_hold })
    syncKopItem(res.data)

    // Update drawing on map page
    if (!props.isKopItemList) {
      // Updating store item after KopItem On Hold update
      updateDrawingStoreItem(res.data)

      // TODO: Update after KOP Item List add
      // // clear selected kopItems from sidebar because one of selected kopItem may become unavailable to approve/reject
      // // after KopItem On Hold update
      // drawingStore.selectedKopItemIds = []
    }

    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'On Hold option updated successfully!'
    })
  } catch (e: any) {
    console.log(e)
    const mes = e?.response?.data ? e?.response?.data : e?.message
    snackbarStore.showMessage({
      color: 'error',
      icon: 'mdi-alert-circle',
      title: 'Error',
      text: `Oops, something went wrong. ${mes}`
    })
  } finally {
    kopItemLoading.value = false
  }
}

const getKopItem = async () => {
  if (!computedKopItemId.value) return

  kopItemLoading.value = true
  try {
    const res = await $getKopItem(computedKopItemId.value)
    syncKopItem(res.data)
  } catch (e: any) {
    console.log(e)
  } finally {
    kopItemLoading.value = false
  }
}

const getKopItemOnApproveReject = async () => {
  await getKopItem()

  // Update drawing on map page
  if (!props.isKopItemList) {
    // Updating store item after updateKopItem
    updateDrawingStoreItem(kopItem.value)

    // TODO: Update after KOP Item List add
    // // clear selected kopItems from sidebar because one of selected kopItem may become unavailable to approve/reject
    // drawingStore.selectedKopItemIds = []
  }
  // Emit onUpdate on Kop Item List
  else {
    emit('onUpdate')
  }
}

const updateKopItem = async () => {
  if (!kopItem.value?.id) return

  updateBtnLoading.value = true
  try {
    kopItem.value.artworks_data = [...kopItemArtworks.value]
    kopItem.value.used_distance = totalUsedWidth.value.toFixed(2)
    const res = await $updateKopItem(kopItem.value.id, kopItem.value)
    syncKopItem(res.data)

    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'KOP Item updated successfully!'
    })

    // Update drawing on map page
    if (!props.isKopItemList) {
      // Updating store item after updateKopItem
      updateDrawingStoreItem(res.data)
      // Save map items - no needed
      // await drawingStore.saveItems()
    }
    // Close dialog and emit onUpdate on Kop Item List
    else {
      emit('onUpdate')
      closeDialog()
    }
  } catch (e: any) {
    console.log(e)
  } finally {
    updateBtnLoading.value = false
  }
}

const onCloseDialog = () => {
}

const handleArtworkRendered = () => {
  artworkLoading.value = false
  totalPages.value = artworkPdf.value?.pageCount || 1
}

const closeDialog = () => {
  computedKopItemDialogVisible.value = false
}

const handleKeydown = (e: KeyboardEvent) => {
  // Close the dialog if the ESC key is pressed
  if (e.key === 'Escape' && !!computedKopItemDialogVisible.value) {
    closeDialog()
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
  getKopItem()
})

// Cleanup event listener when component unmounts
onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown)
})

// Reusable function to silently update the query without causing a page reload, usable in onMounted and navigation guard methods
const updateQueryParameters = (updater: (query: Record<string, any>) => void): void => {
  const newQuery: Record<string, any> = { ...route.query }
  updater(newQuery)
  router.replace({ query: newQuery })
}

// Function to delete the 'show-kop-item-activity' query parameter
const deleteShowKopItemActivityQuery = (): void => {
  updateQueryParameters((newQuery) => {
    delete newQuery['show-kop-item-activity']
  })
}

// Function to set the 'kop-item' query parameter
const setKopItemQuery = (): void => {
  updateQueryParameters((newQuery) => {
    newQuery['kop-item'] = computedKopItemId.value
  })
}

// Function to clear the 'kop-item' query parameter
const clearKopItemQuery = (): void => {
  updateQueryParameters((newQuery) => {
    delete newQuery['kop-item']
  })
}

watch(() => computedKopItemDialogVisible.value, async () => {
  // on show Dialog
  if (!!computedKopItemDialogVisible.value) {
    setKopItemQuery()

    await getKopItem()
    await getKopItemOptions()

    if (props.isKopItemList) {
      await getKopItemProject()
    }

    if (showKopItemActivityQuery.value === 'true') {
      // Show KopItem Activity sidebar after KopItem Modal fully loads
      await nextTick(() => {
        showSidebar.value = true // Set sidebar to be visible
        deleteShowKopItemActivityQuery() // Remove any existing queries for showing KopItem Activity
      })
    }
  }
  // on hide Dialog
  else {
    clearKopItemQuery()
  }
})

const kopItemArtworkCount = computed(() => kopItemArtworks.value.length)

const kopItemRealWidth = computed(() =>
  Number((kopItem.value?.kop_item_real_width || 0).toFixed(2))
)

// KOP Item Real Width + Buffer
const kopItemMaxAllowedWidth = computed(() => {
  return Number(kopItemRealWidth.value) + Number(kopItem.value?.kop_item_buffer || 0)
})

const totalUsedWidth = computed(() =>
  kopItemArtworks.value.reduce((sum, a) => sum + a.width * a.qty, 0)
)

const usedPercentage = computed(() => {
  const max = kopItemMaxAllowedWidth.value || 1
  return Math.min((totalUsedWidth.value / max) * 100, 100)
})

const isOverLimit = computed(() =>
  totalUsedWidth.value > kopItemMaxAllowedWidth.value
)

// Safe count update
const handleArtworkCountChange = (rawCount: unknown) => {
  const parsed = Number(rawCount)

  // Clamp between 1 and 100
  const newCount = Math.min(Math.max(parsed, 1), 100)

  const current = kopItemArtworks.value

  if (newCount > current.length) {
    for (let i = current.length; i < newCount; i++) {
      current.push({ width: 0, height: 0, qty: 1 })
    }
  } else if (newCount < current.length) {
    kopItemArtworks.value = current.slice(0, newCount)
  }
}

const buffer = computed<number>({
  get: (): number => kopItem.value?.kop_item_buffer || 0,
  set: (val: number | string): void => {
    if (val === '') {
      kopItem.value.kop_item_buffer = 0
      return
    }

    // Convert string input to number if needed
    const numericValue = typeof val === 'string' ? parseFloat(val) : val

    // Round to 2 decimal places
    kopItem.value.kop_item_buffer = Math.round(Number(numericValue) * 100) / 100
  }
})
</script>

<template>
  <div>
    <!-- Delete Confirm Dialog available only on the map view not list view -->
    <ConfirmDialog
      v-if="!isKopItemList && isUserCanDeleteKopItem"
      v-model="showDeleteConfirmDialog"
      :title="'Delete KOP Item'"
      :text="'Please, confirm that you would like to delete the KOP Item.'"
      :submit-btn-name="'Confirm'"
      :cancel-btn-name="'Cancel'"
      @onSubmit="drawingStore.deleteSelectedItem(); closeDialog();"
    />
    <!-- /Delete Confirm Dialog available only on the map view not list view -->

    <EditKopItemCode
      v-model="showEditCodeDialog"
      :kop-item="kopItem"
      @updated="onMainFullCodeUpdated"
    />

    <VDialog
      v-model="computedKopItemDialogVisible"
      class="kop-item-modal"
      persistent
      width="920"
      @after-leave="onCloseDialog"
      @update:model-value="getKopItem"
    >

      <DialogCloseBtn
        @click="closeDialog"
      />

      <VCard
        :loading="!!kopItemLoading"
      >
        <template #title>
          <KopItemDialogTitle
            v-model:show-sidebar="showSidebar"
            :kop-item="kopItem"
            :can-edit-code="!isDisabledKopItemFields"
            @getKopItem="getKopItemOnApproveReject"
            @editCode="openEditCodeDialog"
          />
        </template>
        <KopItemDialogHeader
          v-model:kop-item="kopItem"
          :kop-item-loading="kopItemLoading"
          :kop-item-priority-options="kopItemPriorityOptions"
          @onSelectPriority="onSelectPriority"
          @onUpdateKopItemIsOnHold="updateKopItemIsOnHold"
        />
        <KopItemDialogResponsibleBlock
          :kop-item="kopItem"
        />
        <VRow>
          <VCol
            :md="computedOfflineMode ? 12 : 7"
            cols="12">
            <VCardText>
              <VRow>
                <VCol
                  cols="12"
                  sm="4"
                >
                  <VLabel>KOP Item Type:</VLabel>
                  <br/>
                  <b>{{ kopItem.kop_item_type_info?.name }}</b>
                </VCol>
                <VCol
                  cols="12"
                  sm="8"
                >
                  <VSelect
                    v-model="kopItem.functional_area"
                    :items="[...computedFuncAreas]"
                    :disabled="!!isDisabledKopItemFields"
                    item-value="id"
                    item-title="name"
                    label="Functional Area"
                    no-data-text="Functional Areas not found"
                    density="compact"
                  />
                </VCol>
                <!-- Show Width if Individual KOP Item Form -->
                <VCol
                  v-if="kopItem?.kop_item_type_info?.form_type === 2"
                  cols="12"
                  sm="4"
                >
                  <VTextField
                    type="number"
                    v-model="kopItem.width"
                    :disabled="isDisabledKopItemFields || !kopItem.is_custom"
                    label="Width"
                  />
                </VCol>
                <VCol
                  cols="12"
                  :sm="kopItem?.kop_item_type_info?.form_type === 2 ? 4 : 12"
                >
                  <VTextField
                    type="number"
                    v-model="kopItem.height"
                    :disabled="isDisabledKopItemFields || !kopItem.is_custom"
                    label="Height"
                  />
                </VCol>
                <!-- Show Qty(read-only) if Individual KOP Item Form -->
                <VCol
                  v-if="kopItem?.kop_item_type_info?.form_type === 2"
                  cols="12"
                  sm="4"
                >
                  <VTextField
                    type="number"
                    :model-value="kopItem.qty"
                    disabled
                    label="Qty"
                  />
                </VCol>

                <!-- Artworks Data Block -->
                <VCol
                  cols="12"
                  class="py-1"
                >
                  <VTextField
                    v-model="buffer"
                    :disabled="!!isDisabledKopItemFields"
                    label="Buffer"
                    type="number"
                    step="0.1"
                    min="0"
                  />
                </VCol>

                <!-- Visual Width Usage -->
                <VCol cols="12">
                  <div class="d-flex justify-space-between align-center mb-1">
                    <span>Used Width: {{ totalUsedWidth.toFixed(2) }} m</span>
                    <span>Max Allowed: {{ kopItemMaxAllowedWidth.toFixed(2) }} m</span>
                  </div>

                  <VProgressLinear
                    :model-value="usedPercentage"
                    :color="isOverLimit ? 'error' : 'primary'"
                    height="20"
                    rounded
                    striped
                    :bg-color="isOverLimit ? 'error' : 'grey-lighten-2'"
                  >
                    <template #default>
                      <strong class="text-white">
                        {{ usedPercentage.toFixed(1) }}%
                      </strong>
                    </template>
                  </VProgressLinear>

                  <div
                    v-if="isOverLimit"
                    class="mt-2 text-error font-weight-medium text-caption"
                  >
                    Total used width exceeds allowed maximum.
                  </div>
                </VCol>

                <VCol cols="12" sm="6" class="d-flex align-center gap-2">
                  <strong>Artworks:</strong>
                  <VBtn
                    v-if="!isDisabledKopItemFields"
                    size="x-small"
                    class="rounded"
                    :disabled="kopItemArtworkCount >= 100"
                    @click="handleArtworkCountChange(kopItemArtworkCount + 1)"
                    aria-label="Increase artworks count"
                  >
                    <VIcon icon="tabler-plus" />
                    <VMenu
                    activator="parent"
                    location="top center"
                    open-on-hover
                    open-delay="100"
                    close-delay="0"
                    offset="5"
                    class="default-tooltip"
                  >
                    <span class="px-2">
                      Add Artwork
                    </span>
                  </VMenu>
                  </VBtn>
                  <VBtn
                    v-if="!isDisabledKopItemFields"
                    size="x-small"
                    class="rounded"
                    :disabled="kopItemArtworkCount <= 1"
                    @click="handleArtworkCountChange(kopItemArtworkCount - 1)"
                    aria-label="Decrease artworks count"
                  >
                    <VIcon icon="tabler-minus" />
                    <VMenu
                    activator="parent"
                    location="top center"
                    open-on-hover
                    open-delay="100"
                    close-delay="0"
                    offset="5"
                    class="default-tooltip"
                  >
                    <span class="px-2">
                      Delete Artwork
                    </span>
                  </VMenu>
                  </VBtn>
                </VCol>

                <template v-for="(art, index) in kopItemArtworks" :key="index">
                  <VCol
                    cols="12"
                    class="py-1"
                  >
                    <strong class="text-body-2 font-weight-bold">Artwork #{{ index + 1 }}</strong>
                  </VCol>

                  <VCol
                    cols="12"
                    sm="7"
                    class="py-1"
                  >
                    <VTextField
                      v-model="art.width"
                      :disabled="!!isDisabledKopItemFields"
                      label="Width"
                      type="number"
                      step="0.1"
                      min="0"
                    />
                  </VCol>

                  <VCol
                    cols="12"
                    sm="5"
                    class="py-1"
                  >
                    <VTextField
                      v-model="art.qty"
                      :disabled="!!isDisabledKopItemFields"
                      label="Qty"
                      type="number"
                      min="1"
                    />
                  </VCol>
                </template>
                <!-- /Artworks Data Block -->
              </VRow>
            </VCardText>
            <VCardText>
              <VRow>
                <VCol
                  cols="12"
                  sm="6"
                >
                  <VTextarea
                    v-model="kopItem.notes"
                    :disabled="userStore.isCustomer || !kopItem.is_active_campaign"
                    label="TLC Notes"
                  />
                </VCol>
                <VCol
                  cols="12"
                  sm="6"
                >
                  <VTextarea
                    v-model="kopItem.client_notes"
                    :disabled="!userStore.isCustomer || !kopItem.is_active_campaign"
                    label="Client Notes"
                  />
                </VCol>
                <VCol
                  cols="12"
                  sm="6"
                >
                  <VTextarea
                    v-model="kopItem.client_reference"
                    :disabled="!kopItem.is_active_campaign"
                    label="Client Reference"
                  />
                </VCol>
                <VCol
                  cols="12"
                  sm="6"
                >
                  <VTextarea
                    v-model="kopItem.ext_ref_num"
                    :disabled="!kopItem.is_active_campaign"
                    label="Ext Ref #"
                  />
                </VCol>
              </VRow>
            </VCardText>
          </VCol>
          <VCol
            v-if="!computedOfflineMode"
            cols="12"
            md="5"
          >
            <VTabs v-model="photoTab">
              <VTab>Artwork</VTab>
              <VTab>Photos</VTab>
            </VTabs>
            <VWindow v-model="photoTab">
              <VWindowItem class="pa-1 h-100">
                <template v-if="!hasArtworkDownloadSource">
                  <div class="pa-2 text-center">
                    No Artwork
                  </div>
                </template>
                <template v-else>
                  <div class="d-flex justify-end mb-1">
                    <a
                      href="#"
                      @click.prevent="downloadArtwork"
                    >
                      <VProgressCircular v-if="artworkDownloadLoading" size="16" width="2" indeterminate class="mr-1" />
                      Download
                    </a>
                  </div>
                  <VOverlay
                    :model-value="artworkLoading"
                    class="align-center justify-center"
                    contained
                    persistent
                  >
                    <VProgressCircular
                      color="primary"
                      indeterminate
                    />
                  </VOverlay>
                  <VuePdfEmbed
                    v-if="artworkLink"
                    ref="artworkPdf"
                    :page="page"
                    :source="artworkLink"
                    @rendered="handleArtworkRendered"
                  />
                  <div v-else-if="!(artworkLoading)" class="pa-2 text-center">
                    Failed to load PDF preview
                  </div>
                  <div class="pdf-pages">
                    <template v-for="side in kopItemSides + 1">
                      <a :class="{ 'active': side  === page }"
                         class="pdf-page" @click="page = side"
                      >
                        {{ sides[side - 1] }}
                      </a>
                    </template>
                  </div>
                </template>
              </VWindowItem>
              <VWindowItem>
                <div
                  class="mt-2 pl-2 pl-md-0"
                  style="height: 660px; overflow: visible;"
                >
                  <FileManagementPhotosBlock
                    :item-id="computedKopItemId"
                    :item-code="kopItem?.main_full_code || kopItem?.kop_item_code || null"
                    :project-id="kopItem?.campaign || null"
                    :project-type="5"
                    :can-add="!isDisabledKopItemFields"
                    :can-delete="!isDisabledKopItemFields"
                  />
                </div>
              </VWindowItem>
            </VWindow>
          </VCol>
        </VRow>
        <VCardText>
          <div class="d-flex justify-space-between align-center flex-wrap">
            <div
              v-if="kopItem.is_active_campaign"
              class="d-flex flex-wrap"
            >
              <VBtn
                :disabled="!!updateBtnLoading"
                class="mr-2 mt-2 mt-sm-0"
                @click="updateKopItem"
              >
                Update
                <VProgressCircular
                  v-if="!!updateBtnLoading"
                  class="ml-1"
                  color="light"
                  indeterminate
                  size="16"
                  width="2"
                />
              </VBtn>
              <!-- Delete available only on the map view not list view -->
              <VBtn
                v-if="!isKopItemList && isUserCanDeleteKopItem"
                class="mt-2 mt-sm-0"
                variant="tonal"
                @click="showDeleteConfirmDialog = true"
              >
                Delete
              </VBtn>
            </div>
          </div>
        </VCardText>
      </VCard>
    </VDialog>
  </div>
</template>

<style lang="scss">
.kop-item-modal {
  .v-card {
    box-shadow: rgba(0, 0, 0, 0.24) 0 3px 8px !important;
  }

  .ghost-draggable {
    opacity: 0.5;
    background: rgb(var(--v-theme-primary), 0.2);
  }

  .add-pictogram-block {
    &:hover {
      background-color: rgb(var(--v-theme-on-surface), 0.1);
    }
  }
}
.pdf-item {
  padding: 24px;
  width: 100%;
  height: 100%;
  min-height: 500px;
  background: #fff;
  color: #000;
}

.pdf-pages {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  .pdf-page {
    cursor: pointer;
    padding: 2px 8px;
    margin: 0 2px;
    border: 1px solid #DC0000;
    border-radius: 50%;
    &.active {
      background: #DC0000;
      color: #fff;
    }
  }
}
</style>
